import { useEffect, useMemo, useRef, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import {
    AlertTriangle,
    CheckCircle2,
    CheckSquare,
    Download,
    ExternalLink,
    Eye,
    FileText,
    GripVertical,
    Image as ImageIcon,
    Link2,
    MessageSquare,
    Paperclip,
    Pencil,
    Plus,
    Trash2,
    Upload,
    X,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { BOARD_COLUMNS, FUNCTIONAL_STATUSES, functionalClass, PRIORITIES, priorityClass } from "./serviceAuditUtils";

const MAX_OBSERVATION_FILES = 8;
const MAX_OBSERVATION_FILE_BYTES = 15 * 1024 * 1024;
const MAX_OBSERVATION_TOTAL_BYTES = 50 * 1024 * 1024;
const OBSERVATION_FILE_ACCEPT = ".jpg,.jpeg,.png,.webp,.gif,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx";
const createTemporaryId = (prefix) => `tmp_${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

export default function WeeklyAuditBoard({ weeks, selectedWeek, onWeekChange, users, requestedCardId, onRequestedCardHandled, onBoardChanged, canManage = false }) {
    const [board, setBoard] = useState(null);
    const [loading, setLoading] = useState(false);
    const [viewing, setViewing] = useState(null);
    const [editing, setEditing] = useState(null);
    const openedRequestedCard = useRef(null);

    const load = async () => {
        if (!selectedWeek) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/service-audit/weeks/${selectedWeek}/board`);
            setBoard(data);
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedWeek]);

    useEffect(() => {
        if (!requestedCardId) {
            openedRequestedCard.current = null;
            return;
        }
        if (openedRequestedCard.current === requestedCardId || !board?.cards?.length) return;
        const card = board.cards.find((item) => item.card_id === requestedCardId);
        if (card) {
            openedRequestedCard.current = requestedCardId;
            setViewing(card);
            onRequestedCardHandled?.();
        }
    }, [requestedCardId, board, onRequestedCardHandled]);

    const moveCard = async (result) => {
        if (!canManage) return;
        if (!result.destination || result.destination.droppableId === result.source.droppableId) return;
        const cardId = result.draggableId;
        const nextStatus = result.destination.droppableId;
        const previous = board;

        setBoard((current) => ({
            ...current,
            cards: current.cards.map((card) => (
                card.card_id === cardId ? { ...card, column_status: nextStatus } : card
            )),
        }));

        try {
            const { data } = await api.patch(`/service-audit/weekly-cards/${cardId}`, { column_status: nextStatus });
            await load();
            await onBoardChanged?.();
            if (nextStatus === "requiere_revision") {
                const count = data.notifications_sent || 0;
                if (data.notification_error) {
                    toast.warning("Revisión registrada; la alerta quedó pendiente de reintento automático");
                } else {
                    toast.warning(
                        count > 0
                            ? `Se notificó a ${count} desarrollador${count === 1 ? "" : "es"}`
                            : "Revisión registrada; no hay otros desarrolladores activos para notificar",
                    );
                }
            }
        } catch (error) {
            setBoard(previous);
            toast.error(formatApiError(error.response?.data?.detail));
        }
    };

    const cardsByColumn = useMemo(() => {
        const result = Object.fromEntries(BOARD_COLUMNS.map((column) => [column.id, []]));
        for (const card of board?.cards || []) {
            (result[card.column_status] || result.pendiente).push(card);
        }
        return result;
    }, [board]);

    const openEditFromView = () => {
        if (!viewing) return;
        const card = viewing;
        setViewing(null);
        setEditing(card);
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white border border-slate-200 rounded-lg p-4">
                <div>
                    <h3 className="font-semibold text-slate-900">Revisión funcional semanal</h3>
                    <p className="text-sm text-slate-500">Cada semana conserva sus tarjetas, evidencias, checklist y decisiones.</p>
                </div>
                <Select value={selectedWeek || ""} onValueChange={onWeekChange}>
                    <SelectTrigger className="w-full sm:w-72">
                        <SelectValue placeholder="Selecciona una semana" />
                    </SelectTrigger>
                    <SelectContent>
                        {weeks.map((week) => (
                            <SelectItem key={week.week_key} value={week.week_key}>
                                {week.week_key} · {week.stats?.finalized || 0}/{week.stats?.total || 0} finalizados
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {loading ? (
                <div className="py-20 text-center text-slate-400">Cargando tablero…</div>
            ) : (
                <DragDropContext onDragEnd={moveCard}>
                    <div className="flex h-[68vh] min-h-[520px] max-h-[720px] gap-4 overflow-x-auto overflow-y-hidden pb-2" data-testid="service-audit-board">
                        {BOARD_COLUMNS.map((column) => (
                            <Droppable key={column.id} droppableId={column.id}>
                                {(provided, snapshot) => (
                                    <section
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className={`flex h-full w-[300px] shrink-0 flex-col rounded-xl border-t-4 ${column.className} bg-slate-100/80 p-3 ${snapshot.isDraggingOver ? "ring-2 ring-orange-300" : ""}`}
                                    >
                                        <div className="flex items-center justify-between px-1 mb-3">
                                            <div className="flex items-center gap-2">
                                                <span className={`w-2.5 h-2.5 rounded-full ${column.dot}`} />
                                                <h4 className="font-semibold text-sm text-slate-800">{column.label}</h4>
                                            </div>
                                            <span className="text-xs bg-white border border-slate-200 rounded-full px-2 py-0.5 text-slate-500">
                                                {cardsByColumn[column.id]?.length || 0}
                                            </span>
                                        </div>

                                        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
                                            {(cardsByColumn[column.id] || []).map((card, index) => (
                                                <Draggable key={card.card_id} draggableId={card.card_id} index={index} isDragDisabled={!canManage}>
                                                    {(drag, dragState) => (
                                                        <article
                                                            ref={drag.innerRef}
                                                            {...drag.draggableProps}
                                                            {...drag.dragHandleProps}
                                                            className={`relative bg-white border border-slate-200 rounded-lg p-3 pb-11 shadow-sm cursor-grab active:cursor-grabbing select-none ${dragState.isDragging ? "shadow-xl rotate-1 ring-2 ring-orange-300" : "hover:border-orange-200"}`}
                                                            data-testid={`service-audit-card-${card.card_id}`}
                                                        >
                                                            <div className="flex items-start gap-2">
                                                                <GripVertical className="w-4 h-4 mt-0.5 shrink-0 text-slate-300" aria-hidden="true" />
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-[11px] font-semibold text-orange-600">{card.project_snapshot?.deployment_code}</p>
                                                                    <h5 className="text-sm font-semibold text-slate-900 mt-0.5 leading-snug">{card.project_snapshot?.project_name}</h5>
                                                                    <p className="text-xs text-slate-500 mt-1">{card.project_snapshot?.client_name}</p>
                                                                    <ProductionLink card={card} />
                                                                    <div className="flex flex-wrap gap-1.5 mt-3">
                                                                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${priorityClass(card.priority)}`}>
                                                                            {PRIORITIES.find((item) => item.id === card.priority)?.label || card.priority}
                                                                        </span>
                                                                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${functionalClass(card.functional_status)}`}>
                                                                            {FUNCTIONAL_STATUSES.find((item) => item.id === card.functional_status)?.label || card.functional_status}
                                                                        </span>
                                                                    </div>
                                                                    <CardIndicators card={card} />
                                                                </div>
                                                            </div>

                                                            <button
                                                                type="button"
                                                                aria-label={`Ver tarjeta de ${card.project_snapshot?.project_name || "auditoría"}`}
                                                                title="Ver tarjeta"
                                                                className="absolute bottom-2.5 right-2.5 inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:text-orange-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 cursor-pointer"
                                                                onPointerDown={(event) => event.stopPropagation()}
                                                                onClick={() => setViewing(card)}
                                                                data-testid={`service-audit-view-${card.card_id}`}
                                                            >
                                                                <Eye className="w-4 h-4" aria-hidden="true" />
                                                            </button>
                                                        </article>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                            {(cardsByColumn[column.id] || []).length === 0 && (
                                                <div className="border border-dashed border-slate-300 rounded-lg py-10 text-center text-xs text-slate-400">
                                                    Arrastra tarjetas aquí
                                                </div>
                                            )}
                                        </div>
                                    </section>
                                )}
                            </Droppable>
                        ))}
                    </div>
                </DragDropContext>
            )}

            <CardViewDialog
                card={viewing}
                open={Boolean(viewing)}
                onOpenChange={(value) => !value && setViewing(null)}
                users={users}
                onEdit={canManage ? openEditFromView : null}
            />

            {canManage && <CardEditDialog
                card={editing}
                open={Boolean(editing)}
                onOpenChange={(value) => !value && setEditing(null)}
                users={users}
                onSaved={async () => {
                    setEditing(null);
                    await load();
                    await onBoardChanged?.();
                }}
            />}
        </div>
    );
}

function ProductionLink({ card }) {
    const productionUrl = card.project_snapshot?.production_url || card.project?.production_url;
    if (!productionUrl) return null;

    return (
        <a
            href={productionUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex max-w-full items-center gap-1 text-[11px] font-medium text-blue-600 hover:underline"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            title="Abrir proyecto en producción"
        >
            <ExternalLink className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">Producción</span>
        </a>
    );
}


function CardIndicators({ card }) {
    const checklist = card.checklist || [];
    const completed = checklist.filter((item) => item.completed).length;
    const attachmentCount = (card.observations || []).reduce(
        (total, observation) => total + (observation.attachments?.length || 0),
        0,
    );

    return (
        <div className="flex flex-wrap items-center gap-3 mt-3 text-[11px] text-slate-400">
            <span className="flex items-center gap-1"><CheckSquare className="w-3.5 h-3.5" />{completed}/{checklist.length}</span>
            <span className="flex items-center gap-1"><Link2 className="w-3.5 h-3.5" />{card.links?.length || 0}</span>
            <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" />{card.observations?.length || 0}</span>
            {attachmentCount > 0 && <span className="flex items-center gap-1"><Paperclip className="w-3.5 h-3.5" />{attachmentCount}</span>}
        </div>
    );
}

function CardViewDialog({ card, open, onOpenChange, users, onEdit }) {
    if (!card) return null;

    const developerNames = (card.assigned_developer_ids || []).map((userId) => (
        users.find((user) => user.user_id === userId)?.name || userId
    ));
    const column = BOARD_COLUMNS.find((item) => item.id === card.column_status);
    const priority = PRIORITIES.find((item) => item.id === card.priority);
    const functionalStatus = FUNCTIONAL_STATUSES.find((item) => item.id === card.functional_status);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{card.project_snapshot?.project_name}</DialogTitle>
                    <p className="text-sm text-slate-500">
                        {card.project_snapshot?.deployment_code} · {card.project_snapshot?.client_name} · {card.week_key}
                    </p>
                </DialogHeader>

                <div className="grid sm:grid-cols-3 gap-3">
                    <ReadOnlyValue label="Columna / estado" value={column?.label || card.column_status} />
                    <ReadOnlyValue label="Prioridad" value={priority?.label || card.priority} />
                    <ReadOnlyValue label="Funcionamiento" value={functionalStatus?.label || card.functional_status} />
                </div>

                <ReadOnlySection title="Resumen de la revisión">
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{card.summary || "Sin resumen registrado."}</p>
                </ReadOnlySection>

                <div className="grid lg:grid-cols-2 gap-5">
                    <ReadOnlySection title={`Checklist (${(card.checklist || []).filter((item) => item.completed).length}/${card.checklist?.length || 0})`}>
                        <div className="space-y-2">
                            {(card.checklist || []).map((item) => (
                                <div key={item.item_id} className="flex items-start gap-2 text-sm">
                                    <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${item.completed ? "text-emerald-500" : "text-slate-300"}`} />
                                    <span className={item.completed ? "line-through text-slate-400" : "text-slate-700"}>{item.text}</span>
                                </div>
                            ))}
                            {(card.checklist || []).length === 0 && <p className="text-sm text-slate-400">Sin puntos de control.</p>}
                        </div>
                    </ReadOnlySection>

                    <ReadOnlySection title="Links y evidencias">
                        <div className="space-y-2">
                            {(card.links || []).map((link) => (
                                <a
                                    key={link.link_id}
                                    href={link.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                                >
                                    <ExternalLink className="w-4 h-4 shrink-0" />
                                    <span className="truncate">{link.label || link.url}</span>
                                </a>
                            ))}
                            {(card.links || []).length === 0 && <p className="text-sm text-slate-400">Sin links registrados.</p>}
                        </div>
                    </ReadOnlySection>
                </div>

                <ReadOnlySection title="Desarrolladores asignados">
                    <p className="text-sm text-slate-700">
                        {developerNames.length > 0 ? developerNames.join(", ") : "Sin desarrolladores asignados."}
                    </p>
                </ReadOnlySection>

                <ReadOnlySection title="Observaciones de la semana">
                    <ObservationList cardId={card.card_id} observations={card.observations || []} />
                </ReadOnlySection>

                {(card.project_snapshot?.production_url || card.project?.production_url) && (
                    <a
                        href={card.project_snapshot?.production_url || card.project?.production_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline"
                    >
                        <ExternalLink className="w-4 h-4" />Abrir proyecto en producción
                    </a>
                )}

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>Cerrar</Button>
                    {onEdit && <Button onClick={onEdit}><Pencil className="w-4 h-4 mr-2" />Editar tarjeta</Button>}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CardEditDialog({ card, open, onOpenChange, users, onSaved }) {
    const [draft, setDraft] = useState(null);
    const [newObservation, setNewObservation] = useState("");
    const [observationFiles, setObservationFiles] = useState([]);
    const [newChecklist, setNewChecklist] = useState("");
    const [newLink, setNewLink] = useState({ label: "", url: "" });
    const [saving, setSaving] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (card) {
            setDraft({
                column_status: card.column_status || "pendiente",
                priority: card.priority || "media",
                functional_status: card.functional_status || "no_revisado",
                summary: card.summary || "",
                assigned_developer_ids: card.assigned_developer_ids || [],
                links: card.links || [],
                checklist: card.checklist || [],
            });
            setNewObservation("");
            setObservationFiles([]);
            setNewChecklist("");
            setNewLink({ label: "", url: "" });
            if (fileInputRef.current) fileInputRef.current.value = "";
        } else {
            setDraft(null);
        }
    }, [card]);

    if (!draft || !card) return null;

    const addChecklist = (event) => {
        event?.preventDefault();
        const text = newChecklist.trim();
        if (!text) return;

        setDraft((current) => ({
            ...current,
            checklist: [
                ...current.checklist,
                { item_id: createTemporaryId("checklist"), text, completed: false },
            ],
        }));
        setNewChecklist("");
    };

    const addLink = (event) => {
        event?.preventDefault();
        const url = newLink.url.trim();
        if (!url) return;

        setDraft((current) => ({
            ...current,
            links: [
                ...current.links,
                { link_id: createTemporaryId("link"), label: newLink.label.trim(), url },
            ],
        }));
        setNewLink({ label: "", url: "" });
    };

    const toggleDeveloper = (id) => {
        setDraft((current) => ({
            ...current,
            assigned_developer_ids: current.assigned_developer_ids.includes(id)
                ? current.assigned_developer_ids.filter((item) => item !== id)
                : [...current.assigned_developer_ids, id],
        }));
    };

    const selectObservationFiles = (event) => {
        const selected = Array.from(event.target.files || []);
        if (selected.length === 0) return;

        const validFiles = [];
        for (const file of selected) {
            if (file.size > MAX_OBSERVATION_FILE_BYTES) {
                toast.error(`${file.name} supera el límite de 15 MB`);
                continue;
            }
            validFiles.push(file);
        }

        setObservationFiles((current) => {
            const merged = [...current, ...validFiles];
            if (merged.length > MAX_OBSERVATION_FILES) {
                toast.error(`Puedes adjuntar hasta ${MAX_OBSERVATION_FILES} archivos por observación`);
            }
            const limitedByCount = merged.slice(0, MAX_OBSERVATION_FILES);
            const limitedBySize = [];
            let totalBytes = 0;
            for (const file of limitedByCount) {
                if (totalBytes + file.size > MAX_OBSERVATION_TOTAL_BYTES) {
                    toast.error("Los archivos de una observación pueden pesar hasta 50 MB en total");
                    break;
                }
                limitedBySize.push(file);
                totalBytes += file.size;
            }
            return limitedBySize;
        });
        event.target.value = "";
    };

    const save = async () => {
        setSaving(true);
        let reviewWasSaved = false;
        try {
            const { data } = await api.patch(`/service-audit/weekly-cards/${card.card_id}`, {
                ...draft,
                links: draft.links.map(({ link_id, label, url }) => ({
                    link_id: link_id?.startsWith("tmp_") ? null : link_id,
                    label,
                    url,
                })),
                checklist: draft.checklist.map(({ item_id, text, completed, completed_at, completed_by }) => ({
                    item_id: item_id?.startsWith("tmp_") ? null : item_id,
                    text,
                    completed,
                    completed_at,
                    completed_by,
                })),
            });
            reviewWasSaved = true;

            if (newObservation.trim() || observationFiles.length > 0) {
                const formData = new FormData();
                formData.append("text", newObservation.trim());
                observationFiles.forEach((file) => formData.append("files", file));
                await api.post(
                    `/service-audit/weekly-cards/${card.card_id}/observations/with-attachments`,
                    formData,
                );
            }

            if (data.notification_error) {
                toast.warning("Revisión guardada; una alerta quedó pendiente de reintento automático");
            } else if (draft.column_status === "requiere_revision" && card.column_status !== "requiere_revision") {
                const count = data.notifications_sent || 0;
                toast.warning(
                    count > 0
                        ? `Se notificó a ${count} desarrollador${count === 1 ? "" : "es"}`
                        : "Revisión registrada; no hay otros desarrolladores activos para notificar",
                );
            } else {
                toast.success("Revisión semanal guardada");
            }
            await onSaved?.();
        } catch (error) {
            const detail = formatApiError(error.response?.data?.detail);
            toast.error(
                reviewWasSaved
                    ? `La revisión se guardó, pero la observación o sus archivos no pudieron guardarse: ${detail}`
                    : detail,
            );
        } finally {
            setSaving(false);
        }
    };

    const developerUsers = users.filter((user) => user.role === "desarrollador");

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Editar · {card.project_snapshot?.project_name}</DialogTitle>
                    <p className="text-sm text-slate-500">
                        {card.project_snapshot?.deployment_code} · {card.project_snapshot?.client_name} · {card.week_key}
                    </p>
                </DialogHeader>

                {draft.column_status === "requiere_revision" && (
                    <div className="flex gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                        <AlertTriangle className="w-5 h-5 shrink-0" />
                        <p>Esta tarjeta requiere intervención técnica. Al guardar esta transición se genera una notificación para todos los usuarios con rol Desarrollador.</p>
                    </div>
                )}

                <div className="grid sm:grid-cols-3 gap-4">
                    <Field label="Columna / estado">
                        <Select value={draft.column_status} onValueChange={(value) => setDraft({ ...draft, column_status: value })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {BOARD_COLUMNS.map((column) => <SelectItem key={column.id} value={column.id}>{column.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Prioridad">
                        <Select value={draft.priority} onValueChange={(value) => setDraft({ ...draft, priority: value })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {PRIORITIES.map((item) => <SelectItem key={item.id} value={item.id}>{item.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Funcionamiento">
                        <Select value={draft.functional_status} onValueChange={(value) => setDraft({ ...draft, functional_status: value })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {FUNCTIONAL_STATUSES.map((item) => <SelectItem key={item.id} value={item.id}>{item.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                </div>

                <Field label="Resumen de la revisión">
                    <Textarea
                        rows={3}
                        value={draft.summary}
                        onChange={(event) => setDraft({ ...draft, summary: event.target.value })}
                        placeholder="Resultado general, evidencia revisada y decisión…"
                    />
                </Field>

                <div className="grid lg:grid-cols-2 gap-5">
                    <section className="border border-slate-200 rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-sm text-slate-900">Checklist</h4>
                            <Badge variant="secondary">{draft.checklist.filter((item) => item.completed).length}/{draft.checklist.length}</Badge>
                        </div>
                        <div className="space-y-2 max-h-56 overflow-y-auto">
                            {draft.checklist.map((item, index) => (
                                <div key={item.item_id || index} className="flex items-center gap-2">
                                    <Checkbox
                                        checked={item.completed}
                                        onCheckedChange={(checked) => setDraft((current) => ({
                                            ...current,
                                            checklist: current.checklist.map((entry, itemIndex) => (
                                                itemIndex === index ? { ...entry, completed: Boolean(checked) } : entry
                                            )),
                                        }))}
                                    />
                                    <span className={`text-sm flex-1 ${item.completed ? "line-through text-slate-400" : "text-slate-700"}`}>{item.text}</span>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => setDraft((current) => ({
                                            ...current,
                                            checklist: current.checklist.filter((_, itemIndex) => itemIndex !== index),
                                        }))}
                                    >
                                        <Trash2 className="w-4 h-4 text-red-400" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                        <form className="flex gap-2" onSubmit={addChecklist}>
                            <Input
                                value={newChecklist}
                                onChange={(event) => setNewChecklist(event.target.value)}
                                placeholder="Nuevo punto de control"
                                aria-label="Nuevo punto de checklist"
                            />
                            <Button
                                type="submit"
                                size="icon"
                                disabled={!newChecklist.trim()}
                                aria-label="Agregar punto al checklist"
                                title="Agregar punto al checklist"
                            >
                                <Plus className="w-4 h-4" />
                            </Button>
                        </form>
                    </section>

                    <section className="border border-slate-200 rounded-lg p-4 space-y-3">
                        <h4 className="font-semibold text-sm text-slate-900">Links y evidencias</h4>
                        <div className="space-y-2 max-h-44 overflow-y-auto">
                            {draft.links.map((link, index) => (
                                <div key={link.link_id || index} className="flex items-center gap-2 text-sm">
                                    <ExternalLink className="w-4 h-4 text-blue-500" />
                                    <a href={link.url} target="_blank" rel="noreferrer" className="flex-1 truncate text-blue-600 hover:underline">
                                        {link.label || link.url}
                                    </a>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => setDraft((current) => ({
                                            ...current,
                                            links: current.links.filter((_, linkIndex) => linkIndex !== index),
                                        }))}
                                    >
                                        <Trash2 className="w-4 h-4 text-red-400" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                        <form className="grid gap-2" onSubmit={addLink}>
                            <Input
                                value={newLink.label}
                                onChange={(event) => setNewLink((current) => ({ ...current, label: event.target.value }))}
                                placeholder="Etiqueta, por ejemplo: Evidencia QA"
                                aria-label="Etiqueta del link o evidencia"
                            />
                            <div className="flex gap-2">
                                <Input
                                    type="url"
                                    value={newLink.url}
                                    onChange={(event) => setNewLink((current) => ({ ...current, url: event.target.value }))}
                                    placeholder="https://"
                                    aria-label="URL del link o evidencia"
                                    required
                                />
                                <Button
                                    type="submit"
                                    size="icon"
                                    disabled={!newLink.url.trim()}
                                    aria-label="Agregar link o evidencia"
                                    title="Agregar link o evidencia"
                                >
                                    <Plus className="w-4 h-4" />
                                </Button>
                            </div>
                        </form>
                    </section>
                </div>

                <section className="border border-slate-200 rounded-lg p-4 space-y-3">
                    <h4 className="font-semibold text-sm text-slate-900">Desarrolladores asignados</h4>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {developerUsers.map((person) => (
                            <label key={person.user_id} className="flex items-center gap-2 text-sm cursor-pointer">
                                <Checkbox
                                    checked={draft.assigned_developer_ids.includes(person.user_id)}
                                    onCheckedChange={() => toggleDeveloper(person.user_id)}
                                />
                                {person.name}
                            </label>
                        ))}
                        {developerUsers.length === 0 && (
                            <p className="text-sm text-slate-400">Crea o asigna usuarios al rol Desarrollador para recibir alertas.</p>
                        )}
                    </div>
                </section>

                <section className="border border-slate-200 rounded-lg p-4 space-y-4">
                    <h4 className="font-semibold text-sm text-slate-900">Observaciones de la semana</h4>
                    <ObservationList cardId={card.card_id} observations={card.observations || []} />

                    <div className="border-t border-slate-100 pt-4 space-y-3">
                        <Textarea
                            rows={2}
                            value={newObservation}
                            onChange={(event) => setNewObservation(event.target.value)}
                            placeholder="Agregar una observación con autor y fecha…"
                        />

                        <input
                            ref={fileInputRef}
                            type="file"
                            className="hidden"
                            multiple
                            accept={OBSERVATION_FILE_ACCEPT}
                            onChange={selectObservationFiles}
                        />

                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={observationFiles.length >= MAX_OBSERVATION_FILES}
                            >
                                <Upload className="w-4 h-4 mr-2" />Subir archivos
                            </Button>
                            <p className="text-xs text-slate-400">Imágenes, PDF, texto, CSV y Office. Máximo 15 MB por archivo.</p>
                        </div>

                        {observationFiles.length > 0 && (
                            <div className="grid sm:grid-cols-2 gap-2">
                                {observationFiles.map((file, index) => (
                                    <div key={`${file.name}-${file.lastModified}-${index}`} className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                                        <AttachmentIcon contentType={file.type} />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm text-slate-700 truncate">{file.name}</p>
                                            <p className="text-xs text-slate-400">{formatBytes(file.size)}</p>
                                        </div>
                                        <Button
                                            type="button"
                                            size="icon"
                                            variant="ghost"
                                            className="h-8 w-8"
                                            onClick={() => setObservationFiles((current) => current.filter((_, fileIndex) => fileIndex !== index))}
                                            aria-label={`Quitar ${file.name}`}
                                        >
                                            <X className="w-4 h-4 text-slate-500" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
                    <Button onClick={save} disabled={saving}>
                        {saving ? "Guardando…" : <><CheckCircle2 className="w-4 h-4 mr-1" />Guardar revisión</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ObservationList({ cardId, observations }) {
    if (!observations.length) {
        return <p className="text-sm text-slate-400">Sin observaciones registradas.</p>;
    }

    return (
        <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
            {observations.map((item) => (
                <div key={item.observation_id} className="border-l-2 border-orange-300 pl-3 space-y-2">
                    {item.text && <p className="text-sm text-slate-700 whitespace-pre-wrap">{item.text}</p>}
                    {(item.attachments || []).length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {item.attachments.map((attachment) => (
                                <Button
                                    key={attachment.attachment_id}
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-auto max-w-full justify-start py-2"
                                    onClick={() => downloadAttachment(cardId, attachment)}
                                >
                                    <AttachmentIcon contentType={attachment.content_type} />
                                    <span className="ml-2 max-w-[240px] truncate">{attachment.filename}</span>
                                    <span className="ml-2 text-[10px] text-slate-400">{formatBytes(attachment.size)}</span>
                                    <Download className="w-3.5 h-3.5 ml-2 shrink-0" />
                                </Button>
                            ))}
                        </div>
                    )}
                    <p className="text-xs text-slate-400">
                        {item.created_by_name || "Usuario"} · {new Date(item.created_at).toLocaleString("es-EC")}
                    </p>
                </div>
            ))}
        </div>
    );
}

async function downloadAttachment(cardId, attachment) {
    try {
        const { data } = await api.get(
            `/service-audit/weekly-cards/${cardId}/attachments/${attachment.attachment_id}`,
            { responseType: "blob" },
        );
        const blob = data instanceof Blob ? data : new Blob([data], { type: attachment.content_type });
        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = objectUrl;
        anchor.download = attachment.filename || "archivo";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (error) {
        toast.error(formatApiError(error.response?.data?.detail) || "No se pudo descargar el archivo");
    }
}

function AttachmentIcon({ contentType }) {
    if ((contentType || "").startsWith("image/")) {
        return <ImageIcon className="w-4 h-4 shrink-0 text-blue-500" />;
    }
    return <FileText className="w-4 h-4 shrink-0 text-orange-500" />;
}

function formatBytes(value) {
    const bytes = Number(value || 0);
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function ReadOnlyValue({ label, value }) {
    return (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
            <p className="text-xs font-medium text-slate-500">{label}</p>
            <p className="text-sm font-semibold text-slate-800 mt-1">{value || "—"}</p>
        </div>
    );
}

function ReadOnlySection({ title, children }) {
    return (
        <section className="border border-slate-200 rounded-lg p-4 space-y-3">
            <h4 className="font-semibold text-sm text-slate-900">{title}</h4>
            {children}
        </section>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label>{label}</Label>
            <div className="mt-1.5">{children}</div>
        </div>
    );
}
